import { useMemo } from 'react';
import {
  buildToolCallUpstreamSamples,
  useWorkflowUpstreamSampleContext,
} from './use-tool-call-upstream-samples';
import {
  nodeHasUpstreamRefs,
  nodeRefIssues,
} from '../components/node/edit-strategies/node-ref-issues';
import type { ToolInputRefIssue } from '../components/node/edit-strategies/tool-input-ref-issues';

/** Node id to the refs on it that no longer read anything. Absent means clean. */
export type WorkflowRefIssuesByNodeId = ReadonlyMap<
  string,
  readonly ToolInputRefIssue[]
>;

const NO_ISSUES: WorkflowRefIssuesByNodeId = new Map();

/**
 * Every node in the graph whose refs have gone stale.
 *
 * A ref breaks without anyone touching the node that holds it — rename a
 * dataset column, change an agent's output shape, delete the step in between —
 * so the node goes on looking configured and the first sign of trouble is a run
 * that wrote a blank. The drawer says so once opened; this is what lets the
 * canvas say it without opening anything.
 *
 * One pass over the graph rather than a hook per node. The upstream context is
 * gathered once and the rest is pure, so the cost is a walk of the ancestors of
 * each node that actually holds a ref — nodes that hold none are skipped before
 * their samples are ever built, which is most of a typical graph.
 */
export function useWorkflowRefIssues(): WorkflowRefIssuesByNodeId {
  const context = useWorkflowUpstreamSampleContext();
  const {
    graph,
    providers,
    tools,
    agentOutputShapeById,
    agentTitleById,
    payloadFieldsByName,
    datasetColumnsById,
  } = context;

  return useMemo(() => {
    if (graph === null) {
      return NO_ISSUES;
    }
    const byNodeId = new Map<string, readonly ToolInputRefIssue[]>();
    for (const node of graph.nodes) {
      if (!nodeHasUpstreamRefs(node)) {
        continue;
      }
      const samples = buildToolCallUpstreamSamples(
        graph,
        node.id,
        providers,
        tools,
        agentOutputShapeById,
        agentTitleById,
        payloadFieldsByName,
        datasetColumnsById
      );
      const issues = nodeRefIssues(node, samples);
      if (issues.length > 0) {
        byNodeId.set(node.id, issues);
      }
    }
    return byNodeId.size === 0 ? NO_ISSUES : byNodeId;
  }, [
    graph,
    providers,
    tools,
    agentOutputShapeById,
    agentTitleById,
    payloadFieldsByName,
    datasetColumnsById,
  ]);
}
